import React, { useEffect, useState, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import Select, { Theme } from "react-select";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { API_BASE_URL } from "../../../environment";
import {
  showErrorAlert,
  showSuccessAlert,
  showInfoAlert,
} from "../../uiInterface/base-ui/alertservice";
import { AuthContext } from "../../../context/authcontext";
import Preloader from "../../../core/common/pre";
import { languageOptions } from "../../../components/language";
import { timezoneOptions } from "../../../components/timezone";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";

interface Option {
  value: string;
  label: string;
}

const Localization: React.FC = () => {
  const route = all_routes;
  const primaryColor = "#E41F07";
  const [isLoading, setIsLoading] = useState(false);
  const customSelectTheme = (theme: Theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary: primaryColor,
      primary25: "transparent",
      neutral20: "#ced4da",
      neutral30: "#ced4da",
    },
  });

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      borderColor: "#ced4da",
      boxShadow: "none",
      "&:hover": { borderColor: "#ced4da" },
    }),
    option: (base: any, { isSelected, isFocused }: any) => ({
      ...base,
      backgroundColor: isSelected ? primaryColor : "transparent",
      color: isSelected ? "#fff" : isFocused ? primaryColor : "#212529",
      cursor: "pointer",
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: ".375rem .75rem",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#6c757d",
      "&:hover": { color: "#6c757d" },
    }),
    clearIndicator: (base: any) => ({
      ...base,
      color: "#6c757d",
      "&:hover": { color: "#6c757d" },
    }),
  };

  const { selectedTenantId } = useContext(AuthContext);
  const [language, setLanguage] = useState<Option | null>(null);
  const [timezone, setTimezone] = useState<Option | null>(null);
  const [initialLanguage, setInitialLanguage] = useState<string | null>(null);
  const [initialTimezone, setInitialTimezone] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const dateOptions: Option[] = [
    { value: "22-Jul-2023", label: "22 Jul 2023" },
    { value: "Jul-22-2023", label: "Jul 22 2023" },
  ];
  const timeFormatOptions: Option[] = [
    { value: "12-hours", label: "12 Hours" },
    { value: "24-hours", label: "24 Hours" },
  ];
  const yearOptions: Option[] = [
    { value: "2023", label: "2023" },
    { value: "2022", label: "2022" },
  ];
  const monthOptions: Option[] = [
    { value: "January", label: "January" },
    { value: "February", label: "February" },
    { value: "March", label: "March" },
  ];
  const country: Option[] = [
    { value: "India", label: "India" },
    { value: "United States Of America", label: "United States Of America" },
  ];
  const symbols: Option[] = [
    { value: "$", label: "$" },
    { value: "€", label: "€" },
  ];
  const symbolsandvalue: Option[] = [
    { value: "$100", label: "$100" },
    { value: "$400", label: "$400" },
  ];
  const dot: Option[] = [{ value: ".", label: "." }];
  const comma: Option[] = [{ value: ",", label: "," }];
  const permissionforcountry: Option[] = [
    { value: "Allow All Country", label: "Allow All Country" },
    { value: "Deny All Country", label: "Deny All Country" },
  ];

  useEffect(() => {
    if (!selectedTenantId) return;

    const fetchLocalization = async () => {
      const authHeader = localStorage.getItem("authCred");
      if (!authHeader) return;

      setIsLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/tenant/${selectedTenantId}/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        });
        if (!res.ok) {
          console.error("Error fetching localization settings", res.statusText);
          return;
        }
        const data = await res.json();

        setInitialLanguage(data.language);
        setInitialTimezone(data.time_zone);

        const langOpt =
          languageOptions.find((opt) => opt.value === data.language) || null;
        const tzOpt =
          timezoneOptions.find((opt) => opt.value === data.time_zone) || null;
        setLanguage(langOpt);
        setTimezone(tzOpt);
      } catch (error) {
        console.error("Error loading localization settings", error);
        await showErrorAlert("Failed to load localization settings.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocalization();
  }, [selectedTenantId]);

  const hasChanges = useMemo(() => {
    return (
      (language?.value || null) !== initialLanguage ||
      (timezone?.value || null) !== initialTimezone
    );
  }, [language, timezone, initialLanguage, initialTimezone]);

  const handleSave = async () => {
    if (!selectedTenantId) return;
    const authHeader = localStorage.getItem("authCred");
    if (!authHeader) return;
    if (!hasChanges) {
      showInfoAlert("No changes made.");
      return;
    }
    try {
      setIsSaving(true);
      const res = await fetch(`${API_BASE_URL}/tenant/${selectedTenantId}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
        body: JSON.stringify({
          language: language?.value,
          time_zone: timezone?.value,
        }),
      });
      if (!res.ok) throw new Error("Failed to update localization settings.");
      showSuccessAlert("Localization settings updated successfully.");
      setInitialLanguage(language?.value || null);
      setInitialTimezone(timezone?.value || null);
    } catch (error) {
      console.error(error);
      showErrorAlert("Failed to save localization settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLanguage(
      languageOptions.find((opt) => opt.value === initialLanguage) || null
    );
    setTimezone(
      timezoneOptions.find((opt) => opt.value === initialTimezone) || null
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {isLoading && <Preloader />}
        <div className="row">
          <div className="col-md-12">
            {/* Page Header */}
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Settings</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            {/* /Page Header */}
            {/* Settings Menu */}
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <SettingsTabsNavigation
                  activeTab="organization"
                  variant="bottom"
                  includeIcons={true}
                  context="organization"
                />
              </div>
            </div>
            {/* /Settings Menu */}
            <div className="row">
              <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                {/* Settings Sidebar */}
                <div className="card">
                  <div className="card-body">
                    <div className="settings-sidebar">
                      <h4 className="fw-semibold mb-3">
                        Organization Settings
                      </h4>
                      <div className="list-group list-group-flush settings-sidebar">
                        <Link to={route.companySettings} className="fw-medium">
                          General Settings
                        </Link>
                        {/* <Link
                          to={route.localization}
                          className="fw-medium active"
                        >
                          Localization
                        </Link> */}
                        {/*
                        ...other sidebar links...
                        */}
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Settings Sidebar */}
              </div>
              <div className="col-xl-9 col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-3">
                      <h4 className="fw-semibold mb-1">Localization</h4>
                      <p>Provide the basic information below</p>
                    </div>
                    <form>
                      <div className="border-bottom mb-3">
                        <div className="row">
                          <div className="col-md-8">
                            <div className="mb-3">
                              <h6>Language</h6>
                              <p>Select Language of the website</p>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <Select<Option, false>
                              options={languageOptions}
                              placeholder="Select Language"
                              isClearable
                              theme={customSelectTheme}
                              styles={customSelectStyles}
                              value={language}
                              onChange={(opt) => setLanguage(opt)}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-8">
                            <div className="mb-3">
                              <h6>Timezone</h6>
                              <p>Select Timezone for displaying dates</p>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <Select<Option, false>
                              options={timezoneOptions}
                              placeholder="Select Timezone"
                              isClearable
                              theme={customSelectTheme}
                              styles={customSelectStyles}
                              value={timezone}
                              onChange={(opt) => setTimezone(opt)}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary me-2"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSave}
                          disabled={isSaving || !hasChanges}
                        >
                          {isSaving ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Localization;
